import type { Line } from "../../../components/kebabMenu/KebabMenu";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { FastProject, Role } from "../../../types/interfaces";
import { BiArchive, BiEditAlt, BiExport, BiTag, BiTrash } from "react-icons/bi";
import { MdContentCopy, MdPersonRemove } from "react-icons/md";
import { HeaderSpan } from "../SelectProjectUtils";
import { WrappedKebabMenu } from "../components/WrappedKebabMenu";

import { IconType } from "react-icons";
import { TranslationKey } from "../../../types/modulesInterfaces";
import TranslatableText from "../../../components/translations/TranslatableText";
import { useGetCountriesQuery } from "@hooks/index";

interface UseProjectTableReturn {
    columns: ColumnDef<FastProject, any>[];
}

interface TableInfo {
    row: { original: FastProject };
    getValue: () => any;
}

interface ProjectTableActions {
    onDuplicate: (project: FastProject) => void;
    onRemove: (project: FastProject) => void;
    onDelete: (project: FastProject) => void;
    onEdit: (project: FastProject) => void;
    onFinalize: (project: FastProject) => void;
    onEditTags: (project: FastProject) => void;
    onRestore: (id: number) => void;
    onExport: (project: FastProject) => void;
}

enum ActionLineId {
    DUPLICATE = 1,
    EDIT = 2,
    DELETE = 3,
    REMOVE_USER = 4,
    FINALIZE = 5,
    EDIT_TAGS = 6,
    RESTORE = 7,
    EXPORT = 8
}

// this hook is used to create the table columns
export const useProjectTable = (actions: ProjectTableActions): UseProjectTableReturn => {
    const navigate = useNavigate();
    const handleSelectedProject = useCallback((info: TableInfo) => {
        const project = info?.row?.original;
        navigate(`/project/${project.id}/activities`);
    }, [navigate]);

    const { data: countries } = useGetCountriesQuery();

    const columnHelper = createColumnHelper<FastProject>();

    const columns = useMemo(() => {
        // Helper function to create action lines
        const createActionLine = (
            id: ActionLineId, 
            labelKey: string, 
            Icon: IconType, 
            clickHandler: () => void, 
            activeColor?: string
        ): Line => ({
            id,
            label: labelKey as TranslationKey,
            icon: Icon,
            clickHandler,
            ...(activeColor && { activeColor })
        });

        return [
            columnHelper.accessor("name", {
                cell: (info) => {
                    const value = info.getValue();
                    const isFinalized = info?.row?.original.is_finalized;
                    return (
                        <div
                            className="fs-9 clickable span"
                            onClick={() => handleSelectedProject(info)}
                        >
                            {value} {isFinalized ? <TranslatableText translationKey="project.finalized" /> : null}
                        </div>
                    );
                },
                header: () => <HeaderSpan name="project.project" />,
                enableSorting: true,
                sortingFn: "alphanumeric",
            }),
            columnHelper.accessor("country", {
                cell: (info) => {
                    const value = +info.getValue();
                    const country = countries?.find((c: { id: number; name: string }) => c.id === value);
                    return (
                        <div className="fs-9 span">
                            {country?.name}
                        </div>
                    );
                },
                header: () => <HeaderSpan name="main.location" />,
                enableSorting: true,
                sortingFn: "alphanumeric",
            }),
            columnHelper.accessor("role", {
                cell: (info) => {
                    const roles = info.getValue();
                    if (Array.isArray(roles)) {
                        return (
                            <div className="fs-9 span">
                                {roles.join(", ")}
                            </div>
                        );
                    }
                    return roles;
                },
                header: () => <HeaderSpan name="main.role" />,
                enableSorting: true,
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue(columnId) as string[];
                    const b = rowB.getValue(columnId) as string[];
                    return a[0].localeCompare(b[0]);
                }
            }),
            columnHelper.accessor("updated_at", {
                cell: (info) => {
                    const lastUpdated = info.getValue();
                    const date = new Date(lastUpdated);
                    const isValidDate = !isNaN(date.getTime());
                    return (
                        <div className="fs-9 span">
                            {isValidDate ? date.toLocaleString() : "-"}
                        </div>
                    );
                },
                header: () => <HeaderSpan name="main.last_updated" />,
                enableSorting: true,
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue(columnId) as string;
                    const b = rowB.getValue(columnId) as string;
                    return a.localeCompare(b);
                }
            }),
            columnHelper.accessor("created_at", {
                cell: (info) => {
                    const createdAt = info.getValue();
                    const date = new Date(createdAt);
                    const isValidDate = !isNaN(date.getTime());
                    return (
                        <div className="fs-9 span">
                            {isValidDate ? date.toLocaleDateString('en-GB') : "-"}
                        </div>
                    );
                },
                header: () => <HeaderSpan name="main.created_at" />,
                enableSorting: true,
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue(columnId) as string;
                    const b = rowB.getValue(columnId) as string;
                    return a.localeCompare(b);
                }
            }),
            columnHelper.display({
                id: "edit",
                cell: (info) => {
                    const project = info?.row?.original;
                    const { role: roles, is_finalized: isFinalized, is_archived: isArchived } = project;
                    const isAdminOrAnalyst = roles?.includes(Role.ADMIN) || roles?.includes(Role.ANALYST);

                    // Core action lines
                    const deleteAction = createActionLine(
                        ActionLineId.DELETE,
                        isArchived ? "main.delete_permanently" : "main.delete",
                        BiTrash,
                        () => actions.onDelete(project),
                        "var(--error)"
                    );

                    const editAction = createActionLine(
                        ActionLineId.EDIT,
                        "main.edit",
                        BiEditAlt,
                        () => actions.onEdit(project)
                    );

                    const finalizeAction = createActionLine(
                        ActionLineId.FINALIZE,
                        "main.finalize",
                        BiArchive,
                        () => actions.onFinalize(project)
                    );

                    // Additional actions
                    const removeUserAction = createActionLine(
                        ActionLineId.REMOVE_USER,
                        "main.remove_me_from_project",
                        MdPersonRemove,
                        () => actions.onRemove(project)
                    );

                    const duplicateAction = createActionLine(
                        ActionLineId.DUPLICATE,
                        "main.duplicate",
                        MdContentCopy,
                        () => actions.onDuplicate(project)
                    );

                    const editTagsAction = createActionLine(
                        ActionLineId.EDIT_TAGS,
                        "project.edit_tags",
                        BiTag,
                        () => actions.onEditTags(project)
                    );

                    const restoreAction = createActionLine(
                        ActionLineId.RESTORE,
                        "main.restore",
                        BiArchive,
                        () => actions.onRestore(project.id)
                    );

                    const exportAction = createActionLine(
                        ActionLineId.EXPORT,
                        "project.export_project",
                        BiExport,
                        () => actions.onExport(project)
                    );

                    // Determine which actions to show based on project state
                    const actionLines = isArchived
                        ? [deleteAction, restoreAction]
                        : [
                            duplicateAction,
                            exportAction,
                            removeUserAction,
                            ...(isAdminOrAnalyst && !isFinalized
                                ? [deleteAction, editAction, finalizeAction]
                                : []),
                            editTagsAction
                        ];

                    return <WrappedKebabMenu lines={actionLines} />;
                },
                header: () => (
                    <div className="w-100 text-center">
                        <HeaderSpan name="main.edit" />
                    </div>
                ),
            }),
        ];
    }, [columnHelper, handleSelectedProject, actions, countries]);

    return {
        columns
    };
}; 